import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listComprobantesByOperacion } from '@/shared/api/controllers/comprobantePagos';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertTriangle,
  ArrowLeft,
  Ban,
  Box,
  CheckCheck,
  Check,
  ClipboardList,
  Download,
  Eye,
  FileText,
  Landmark,
  Loader2,
  Mail,
  MessageCircle,
  Pencil,
  Repeat,
  Wallet,
  X,
} from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { ErrorDialog } from '@/shared/components/ui/status-dialog';
import { cn } from '@/shared/lib/cn';
import { currencyFromSp, formatMoney, parseMoney } from '@/shared/lib/money';
import { useSession, useCanActOnVenta, useIsReadOnly } from '@/shared/auth/SessionProvider';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { resumenOperacionesClient } from '@/shared/api/controllers/resumenOperaciones';
import {
  listDetallesByOperacion,
  type DetalleOperacionesItem,
} from '@/shared/api/controllers/detalleOperaciones';
import { contactosClient } from '@/shared/api/controllers/contactos';
import {
  calcStockCotizable,
  inventarioClient,
  type InventarioItem,
} from '@/shared/api/controllers/inventario';
import {
  isCampaniaVigente,
  listActiveCampanias,
  storedDecimalToPercent,
} from '@/shared/api/controllers/campanias';
import {
  listActiveArticulos,
  articuloImageUrl,
} from '@/shared/api/controllers/articulos';
import { authHeaders } from '@/shared/api/client';
import { generateCotizacionPDF, cotizacionFileName } from './pdf/cotizacion';
import { listPendingByOperacion } from '@/shared/api/controllers/autorizacionDescuentos';
import {
  anularVenta,
  enviarCotizacionEmail,
  shareCotizacionLink,
} from '@/shared/api/controllers/ventas';
import {
  calcInvoiceTotals,
  calcLineTotals,
  parseIvaRateString,
  type IvaRate,
} from './lib/calc';
import type { Moneda } from './components/MoneyCell';
import { MoneyCell } from './components/MoneyCell';
import { InfoField } from './components/InfoField';
import { ProcesandoOverlay } from './components/ProcesandoOverlay';
import { SinStockDialog, type StockShortage } from './components/SinStockDialog';
import { StageCard } from './components/StageCard';
import { DespachoArticulosDialog } from './components/DespachoArticulosDialog';
import { DespachoDetallesDialog } from './components/DespachoDetallesDialog';
// Reuse the mantenimiento horno-swap flow verbatim — ventas users can now
// request the swap from the Despacho stage too (same server endpoint + gate).
import { CambiarHornoDialog } from '@/features/mantenimiento/components/CambiarHornoDialog';
import { listMantenimientoDetalles } from '@/features/mantenimiento/api';
import { PuestaMarchaDetallesDialog } from './components/PuestaMarchaDetallesDialog';
import { DetalleLineasDialog, type DetalleLineaRow } from './components/DetalleLineasDialog';
import { EditarCotizacionDialog, type EditorLineSeed } from './EditarCotizacionDialog';
import { mesesGarantiaDe } from './components/Garantia';
import { EmailConfirmDialog } from '@/shared/components/ui/email-confirm-dialog';
import { VerFacturasDialog } from './components/VerFacturasDialog';
import type { DetalleNavState } from './detalleNavState';
import { pctEfectivoRecargoMp } from './lib/pagosCalc';
import { esClienteExterior } from './lib/ivaExterior';
import { EcheqSaldoTag, saldoEnDespacho, useSaldosEcheq } from '@/shared/components/EcheqSaldoTag';

/**
 * Detalle Venta — Stage-3 port of `Screen_DetalleVenta.pa.yaml`.
 *
 * Hub-style layout: one row per lifecycle stage (Cotización, Pagos,
 * Facturación, Despacho, Puesta en marcha). Later stages stay disabled
 * until the cotización advances. Only Cotización is live-wired today;
 * Facturación / Despacho / Puesta en marcha route to placeholders that
 * Stages 4-6 will fill in.
 */
export function DetalleVentaPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const { profile } = useSession();

  // Gallery can hand us the already-loaded header via location state so
  // the hub renders synchronously instead of waiting on a round-trip.
  const seededHeader = (location.state as DetalleNavState | null)?.header ?? null;

  // --- data loads ---
  // Country in every queryKey so AR/MX caches stay isolated even though
  // the venta `id` already implies country (a venta belongs to one
  // country). Belt-and-suspenders so direct cache reads can't return
  // wrong-country data.
  const { config: activeCountry } = useActiveCountry();
  const countryCode = activeCountry.code;
  // A venta belongs to one country, so switching the active country while
  // viewing a detail page would leave us asking for a venta that the
  // backend will refuse (times out / "operación no encontrada"). Bounce
  // the user back to the ventas list on country change.
  const mountedCountryRef = useRef(countryCode);
  useEffect(() => {
    if (mountedCountryRef.current !== countryCode) {
      navigate('/ventas', { replace: true });
    }
  }, [countryCode, navigate]);
  // Suppress fetches once the active country has diverged from the
  // country we mounted under — the protective navigate runs in an
  // effect, so without this guard the queryKey change fires a fetch
  // against the new country's scope before we redirect, producing a
  // 404 ("sales_headers <id> not found") for an id that lives in the
  // previous country.
  const countryStable = mountedCountryRef.current === countryCode;
  const headerQ = useQuery({
    queryKey: ['detalle-venta', countryCode, 'header', id],
    queryFn: () => resumenOperacionesClient.getItem(id!),
    enabled: Boolean(id) && countryStable,
    initialData: seededHeader && seededHeader.id === id ? seededHeader : undefined,
    initialDataUpdatedAt: seededHeader && seededHeader.id === id ? Date.now() : undefined,
    // Trust the seeded copy for 30s; after that, refetch to catch status
    // transitions caused by other users.
    staleTime: 30_000,
  });
  const linesQ = useQuery({
    queryKey: ['detalle-venta', countryCode, 'lines', id],
    queryFn: () => listDetallesByOperacion(id!),
    enabled: Boolean(id) && countryStable,
  });
  const header = headerQ.data;
  const clienteId = header?.fields.IDCliente_RO ?? null;

  const contactoQ = useQuery({
    queryKey: ['detalle-venta', countryCode, 'contacto', clienteId],
    queryFn: () => contactosClient.getItem(clienteId!),
    enabled: Boolean(clienteId),
  });
  // Campaña map is only needed inside the Ver detalle dialog (to paint the
  // green "Campaña X%" badge on each line) and the editor (to default new
  // lines to their campaign pct). Gate the fetch on either being open so
  // we don't burn a query on every hub load.
  const [detalleOpen, setDetalleOpen] = useState(false);
  const [editarOpen, setEditarOpen] = useState(false);
  const [verFacturasOpen, setVerFacturasOpen] = useState(false);
  const [despachoDetallesOpen, setDespachoDetallesOpen] = useState(false);
  const [despachoArticulosOpen, setDespachoArticulosOpen] = useState(false);
  const [puestaMarchaOpen, setPuestaMarchaOpen] = useState(false);
  const [cambiarHornoOpen, setCambiarHornoOpen] = useState(false);
  // Despacho detalle lines (subtotal/IVA per horno) for the swap dialog — only
  // fetched when it opens, so the hub load isn't charged for it.
  const cambiarHornoDetallesQ = useQuery({
    queryKey: ['detalle-venta', countryCode, 'cambiar-horno-detalles', id],
    queryFn: () => listMantenimientoDetalles(id!),
    enabled: Boolean(id) && cambiarHornoOpen,
  });
  const campaniasQ = useQuery({
    queryKey: ['detalle-venta', countryCode, 'campanias'],
    queryFn: listActiveCampanias,
    staleTime: 5 * 60_000,
    enabled: detalleOpen || editarOpen,
  });
  const autorizacionesQ = useQuery({
    queryKey: ['detalle-venta', countryCode, 'autorizaciones', id],
    queryFn: () => listPendingByOperacion(id!),
    enabled: Boolean(id) && countryStable,
  });

  // --- derived ---
  const campaignByArticulo = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of campaniasQ.data ?? []) {
      // Only currently-vigente campaigns — the edit dialog uses this map to
      // (re)apply campaign discounts, and the server now rejects claims
      // outside the date window, so an expired/future campaign must not be
      // offered here.
      if (!isCampaniaVigente(c.fields)) continue;
      const aid = c.fields.ArticuloID_C;
      const pct = storedDecimalToPercent(c.fields.Descuento_C);
      if (aid && pct != null) {
        // Deterministic on overlap: keep the highest discount.
        const prev = m.get(aid);
        if (prev == null || pct > prev) m.set(aid, pct);
      }
    }
    return m;
  }, [campaniasQ.data]);

  const pendingByDetalle = useMemo(() => {
    const s = new Set<string>();
    for (const a of autorizacionesQ.data ?? []) {
      if (a.fields.IDDetalle_AD) s.add(a.fields.IDDetalle_AD);
    }
    return s;
  }, [autorizacionesQ.data]);

  const lineRows: DetalleLineaRow[] = useMemo(
    () =>
      (linesQ.data ?? [])
        // Skip soft-deleted lines so the preview dialog + totals strip
        // match the server-side header totals after an Edit removes a row.
        .filter((d) => (d.fields.Status_DO ?? 'Activo') !== 'Anulado')
        .map((d) => toLineRow(d, campaignByArticulo, pendingByDetalle)),
    [linesQ.data, campaignByArticulo, pendingByDetalle],
  );

  // Existing motivos keyed by detalleId — the editor pre-fills them so a
  // pre-existing Pendiente Aprobación line doesn't force the user to
  // re-type something they already wrote.
  const motivoByDetalleId = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of autorizacionesQ.data ?? []) {
      if (a.fields.IDDetalle_AD && a.fields.Motivo_AD) {
        m.set(a.fields.IDDetalle_AD, a.fields.Motivo_AD.trim());
      }
    }
    return m;
  }, [autorizacionesQ.data]);

  const editorSeeds: EditorLineSeed[] = useMemo(
    () =>
      (linesQ.data ?? [])
        // Skip already-anulado lines — the editor should show the live set
        // of articles the user is currently working with.
        .filter((d) => d.fields.Status_DO !== 'Anulado')
        .map((d) => {
          const iva: IvaRate = parseIvaRateString(d.fields.IVA_RO);
          const articuloId = d.fields.IDArticulo_DO ?? '';
          const descuento = parseMoney(d.fields.Descuento_RO) ?? 0;
          // A line is "settled" only when there's no pending AD for it.
          // Status_DO alone isn't enough: the supabase abm_status enum
          // collapses 'Pendiente Aprobacion' → 'Activo', so reading just
          // Status_DO would falsely treat pending lines as approved and
          // hide the editor's re-auth badge. Anchor on the AD set —
          // that's the source of truth for "still awaiting approval".
          const hasPendingAd = pendingByDetalle.has(d.id);
          const approvedDescuento = hasPendingAd ? undefined : descuento;
          return {
            detalleId: d.id,
            articuloId,
            articulo: d.fields.Articulo_DO?.trim() || '—',
            monto: parseMoney(d.fields.Monto_RO) ?? 0,
            cantidad: parseMoney(d.fields.Cantidad_RO) ?? 0,
            iva,
            descuento,
            esRegalo: d.fields.EsRegalo_DO === 'SI',
            llevaGarantia: d.fields.LlevaGarantia_DO === 'SI',
            garantiaMeses: mesesGarantiaDe(d.fields.GarantiaMeses_DO),
            garantiaVence: d.fields.GarantiaManual_DO === 'SI' ? (d.fields.GarantiaVence_DO ?? null) : null,
            campaignPct: campaignByArticulo.get(articuloId) ?? null,
            fechaEstimadaIngreso: d.fields.FechaEstimadaIngreso_DO ?? null,
            motivo: motivoByDetalleId.get(d.id) ?? '',
            approvedDescuento,
          };
        }),
    [linesQ.data, campaignByArticulo, motivoByDetalleId, pendingByDetalle],
  );

  const invoice = useMemo(
    () =>
      calcInvoiceTotals(
        lineRows.map((r) => ({
          monto: r.monto,
          cantidad: r.cantidad,
          descuento: r.descuento,
          iva: r.iva,
          esRegalo: r.esRegalo,
        })),
      ),
    [lineRows],
  );

  // Pass the row's stored currency through verbatim — `currencyFromSp`
  // accepts both PG codes ('ARS' / 'MXN' / 'USD') and the legacy
  // SP labels ('Pesos Mexicanos', 'Dolares', …) and falls back to ARS.
  const moneda: Moneda = currencyFromSp(header?.fields.Moneda_RO);
  const status = header?.fields.Status_RO ?? null;
  const saldoEcheq = saldoEnDespacho(useSaldosEcheq(), id ?? '', status);
  const aceptado = header?.fields.Aceptado_RO === 'SI';
  const pagoCargado = header?.fields.PagoCargado_P === 'SI';
  const anulado = status === 'Anulado';
  // Una venta anulada puede seguir teniendo facturas VIVAS ante ARCA (anular
  // es un soft-delete: no toca `cae` ni `nro_comprobante`). La única forma de
  // darlas de baja es la Nota de Crédito, que se emite desde Ver Facturas —
  // así que si dejamos la tarjeta de Facturación deshabilitada, el aviso de
  // "facturas sin venta activa" manda al operador a una pantalla sin salida.
  // Sólo consultamos en ese caso puntual.
  const facturasVivasQ = useQuery({
    queryKey: ['pagos', 'comprobantes-full', String(id ?? '')],
    queryFn: () => listComprobantesByOperacion(id!),
    enabled: Boolean(id) && anulado,
    staleTime: 30_000,
  });
  // Cuenta también las Notas de Crédito, no sólo las facturas sin acreditar.
  // Una NC TOTAL limpia `facturado` en el comprobante padre (así deja de
  // ocupar techo de facturación), así que mirando sólo `Facturado_CP` la
  // tarjeta se volvía a cerrar JUSTO DESPUÉS de emitir la NC — y Ver Facturas
  // es la única pantalla de toda la app que lista una NC emitida (Reportes las
  // excluye y `/ar-factura-data` no las regenera). El operador arreglaba el
  // huérfano y perdía el comprobante de que lo arregló.
  const tieneFacturasVivas = (facturasVivasQ.data ?? []).some(
    (c) =>
      c.fields.Facturado_CP === 'SI' ||
      (c.fields.Status_P ?? '').trim() === 'Nota de Credito',
  );
  const pendingCount = autorizacionesQ.data?.length ?? 0;
  const hasPending = pendingCount > 0;
  const isAdministracion = profile?.perfil === 'administracion';
  // Marketing may act only on its OWN ventas; every other role keeps its
  // existing access. Equivalent to `canAct` for non-marketing roles.
  const canAct = useCanActOnVenta(header?.fields.Vendedor_RO ?? null);
  const isReadOnly = useIsReadOnly();

  // Cambiar horno — same rule as Mantenimiento's despacho card: only while the
  // venta is in Pendiente Despacho (once dispatched it moves to 'Despachado',
  // out of the window), never on Nova ventas (Factura Interna / Caja Diferencia
  // would go inconsistent), never read-only. The swap only FILES an approval;
  // the server re-validates. Ventas users can now request it here too.
  const esNova = (header?.fields.FNova_RO ?? '') === 'SI';
  const canCambiarHorno = !anulado && status === 'Pendiente Despacho' && !esNova && !isReadOnly;
  const monedaCambiarHorno: 'ARS' | 'MXN' | 'USD' =
    (header?.fields.Moneda_RO ?? '') === 'USD'
      ? 'USD'
      : activeCountry.code === 'MX'
        ? 'MXN'
        : 'ARS';
  const porcentajeFacturaVenta = (() => {
    const raw = (header?.fields.PorcentajeFactura_RO ?? '').toString().replace(',', '.');
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? Math.min(100, n) : 100;
  })();
  // Combined gate for "this cotización is waiting on an authorization, so
  // committing it (accept) or sharing it (email / whatsapp / pdf) would
  // expose values that haven't been approved yet". Falls back to the header
  // status when the autorizaciones query hasn't resolved, so the gate is
  // honored even on first paint.
  const isPendingApproval = hasPending || status === 'Pendiente Aprobacion';

  const stages = useMemo(
    () => deriveStageStates({ status, aceptado, pagoCargado, anulado, tieneFacturasVivas }),
    [status, aceptado, pagoCargado, anulado, tieneFacturasVivas],
  );

  // Aceptar stays enabled as soon as the header is loaded. Lines only feed
  // the stock-revalidation pass, which runs after the user confirms — at
  // that point we fall back to fetching them fresh inside the mutation if
  // the background query hasn't resolved yet.
  const canAccept = stages.cotizacion.canAccept && !hasPending && !isAdministracion && canAct;
  const canAnular = stages.cotizacion.canAnular && !isAdministracion && canAct;
  // Edit window extends through Pago Parcial: admins can fix typos /
  // cantidades / discounts at any point until the venta hits Pendiente
  // Despacho (logistics-side). Once stock is committed (Confirmacion de
  // Pago / Pago Parcial) the backend additionally enforces a saldo
  // floor — the new total can't drop below what's already been
  // registered as pago or promesa, and the editor surfaces that limit
  // live so the admin sees it before submitting.
  // Aprobaciones-side: discount changes that exceed the user's monthly
  // allowance create fresh pending AutorizacionDescuentos rows; pre-pago
  // statuses also revert Status_RO to Pendiente Aprobacion, while
  // post-pago statuses keep their current status (the bucket holds
  // stock and a regression would orphan units). Supabase Realtime
  // surfaces every change to admins.
  const isEditableStatus =
    status === 'Cotizado' ||
    status === 'Pendiente Aprobacion' ||
    status === 'Aceptado' ||
    status === 'Pendiente de Pago' ||
    status === 'Confirmacion de Pago' ||
    status === 'Pago Parcial';
  const canEdit = isEditableStatus && !anulado && !isAdministracion && canAct && !linesQ.isPending;

  // --- dialog / mutation state ---
  // `detalleOpen` is declared earlier since it gates the campañas fetch.
  const [confirmAceptarOpen, setConfirmAceptarOpen] = useState(false);
  const [confirmAnularOpen, setConfirmAnularOpen] = useState(false);
  const [enviarEmailOpen, setEnviarEmailOpen] = useState(false);
  const [shortages, setShortages] = useState<StockShortage[] | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Per-action busy flags. We avoid a single shared `pdfBusy` because
  // download / email / WhatsApp all funnel through `buildPdfBlob`, and
  // a single flag would cross-light unrelated buttons (e.g. the
  // Download spinner firing while the user is sharing on WhatsApp).
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [whatsappBusy, setWhatsappBusy] = useState(false);

  // PDF + WhatsApp share. Both fetch the article catalogue on demand
  // (descriptions live there, not on the line) and then build the PDF
  // client-side via jsPDF. The WhatsApp action also opens wa.me with a
  // pre-filled text — the user attaches the freshly-downloaded PDF
  // manually since the wa.me URL scheme can't carry attachments.
  // The caller owns its own busy flag — see `setDownloadBusy` /
  // `setWhatsappBusy` below.
  const buildPdfBlob = async (): Promise<Blob | null> => {
    if (!header || !linesQ.data) {
      toast.error('La cotización aún se está cargando');
      return null;
    }
    try {
      const articulos = await listActiveArticulos();
      // Pre-fetch each line's article image as a data URL. We attempt
      // the fetch for every unique articulo on the cotización rather
      // than gating on `ImagenItemId_ART`, because that field isn't
      // always surfaced reliably on the cached articulo row. The
      // server endpoint short-circuits with 404 for articulos without
      // an image, which we treat as `null`.
      const uniqueArticuloIds = new Set<string>();
      for (const line of linesQ.data) {
        const aid = line.fields.IDArticulo_DO;
        if (aid) uniqueArticuloIds.add(aid);
      }
      const imagesByArticuloId = new Map<string, string>();
      await Promise.all(
        Array.from(uniqueArticuloIds).map(async (articuloId) => {
          const dataUrl = await fetchArticuloImageAsDataUrl(articuloId);
          if (dataUrl) imagesByArticuloId.set(articuloId, dataUrl);
        }),
      );
      return await generateCotizacionPDF({
        header,
        lines: linesQ.data,
        contacto: contactoQ.data ?? null,
        articulos,
        imagesByArticuloId,
        country: countryCode,
      });
    } catch (err) {
      console.error('[DetalleVenta] PDF generation failed:', err);
      toast.error('No se pudo generar el PDF', {
        description: err instanceof Error ? err.message : undefined,
      });
      return null;
    }
  };

  const handleDownloadPDF = async () => {
    if (!header) return;
    if (isPendingApproval) {
      toast.error('No se puede descargar el PDF', {
        description: 'La cotización tiene una aprobación pendiente.',
      });
      return;
    }
    setDownloadBusy(true);
    try {
      const blob = await buildPdfBlob();
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = cotizacionFileName(header);
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } finally {
      setDownloadBusy(false);
    }
  };

  const handleEnviarEmail = async (recipientEmail: string) => {
    if (!header) return;
    if (isPendingApproval) {
      toast.error('No se puede enviar la cotización', {
        description: 'La cotización tiene una aprobación pendiente.',
      });
      return;
    }
    const blob = await buildPdfBlob();
    if (!blob) {
      // buildPdfBlob already toasted the error; just keep the dialog
      // open so the admin can retry without losing their typed
      // recipient.
      return;
    }
    try {
      const result = await enviarCotizacionEmail(id!, {
        recipientEmail,
        recipientName: contactoQ.data?.fields.NombreApellido_C?.trim() ?? '',
        fileName: cotizacionFileName(header),
        pdf: blob,
      });
      if (result.sent) {
        toast.success('Cotización enviada', { description: `A ${result.to}.` });
        setEnviarEmailOpen(false);
      } else {
        toast.error('No se pudo enviar la cotización', {
          description:
            result.reason === 'no_recipient'
              ? 'Falta el correo del destinatario.'
              : friendlyEmailSendError(result.error),
        });
      }
    } catch (err) {
      toast.error('No se pudo enviar la cotización', {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const handleShareWhatsApp = async () => {
    if (!header || !linesQ.data) {
      toast.error('La cotización aún se está cargando');
      return;
    }
    if (isPendingApproval) {
      toast.error('No se puede compartir por WhatsApp', {
        description: 'La cotización tiene una aprobación pendiente.',
      });
      return;
    }
    const celularRaw = contactoQ.data?.fields.Celular_C ?? '';
    // Strip everything that isn't a digit; prepend Argentina country
    // code if missing. wa.me only accepts the international form.
    const digits = celularRaw.replace(/\D/g, '');
    const phone = digits ? (digits.startsWith('54') ? digits : `54${digits}`) : '';

    // Title flips to VENTA once the cotización has been accepted —
    // matches the legacy PowerApps wording and makes the message
    // self-describing for the recipient.
    const esVenta =
      header.fields.Tipo_RO === 'Venta' || header.fields.Aceptado_RO === 'SI';
    const title = esVenta ? 'VENTA FORNITALIA' : 'COTIZACIÓN FORNITALIA';
    const nro = header.fields.NrCotizacion_RO || header.id;
    const cliente =
      contactoQ.data?.fields.NombreApellido_C?.trim() ||
      header.fields.Cliente_RO ||
      '—';
    const fecha = header.fields.Fecha_RO || new Date().toLocaleDateString('es-AR');

    const activeLines = linesQ.data.filter(
      (l) => (l.fields.Status_DO ?? 'Activo') !== 'Anulado',
    );
    const itemLines = activeLines.map((l) => {
      const iva: IvaRate = parseIvaRateString(l.fields.IVA_RO);
      const monto = parseMoney(l.fields.Monto_RO) ?? 0;
      const cantidad = parseMoney(l.fields.Cantidad_RO) ?? 0;
      const descuento = parseMoney(l.fields.Descuento_RO) ?? 0;
      const t = calcLineTotals({
        monto,
        cantidad,
        descuento,
        iva,
        esRegalo: l.fields.EsRegalo_DO === 'SI',
      });
      const articulo = l.fields.Articulo_DO?.trim() || '—';
      const cantStr =
        Math.abs(cantidad - Math.round(cantidad)) < 0.01
          ? String(Math.round(cantidad))
          : cantidad.toFixed(2).replace('.', ',');
      return `• ${articulo} x ${cantStr} — ${formatMoney(t.subtotalDescontado, moneda)}`;
    });
    const totals = calcInvoiceTotals(
      activeLines.map((l) => {
        return {
          monto: parseMoney(l.fields.Monto_RO) ?? 0,
          cantidad: parseMoney(l.fields.Cantidad_RO) ?? 0,
          descuento: parseMoney(l.fields.Descuento_RO) ?? 0,
          iva: parseIvaRateString(l.fields.IVA_RO),
          esRegalo: l.fields.EsRegalo_DO === 'SI',
        };
      }),
    );
    const ivaTotal = totals.iva21 + totals.iva105;

    // QA #5: build + upload the PDF first so the WhatsApp message
    // carries an anonymous OneDrive share link (legacy PowerApps's
    // `MandarLinkdePDF-Fornitalia.tosharelink`). The button shows a
    // spinner while we work; we only open WhatsApp once we have the
    // share URL. If anything fails — PDF gen or upload — we surface
    // a clear error and abort instead of opening a link-less message.
    setWhatsappBusy(true);
    let shareUrl: string;
    try {
      const blob = await buildPdfBlob();
      if (!blob) {
        // buildPdfBlob already toasted; nothing more to add. Don't
        // open WhatsApp without the link.
        setWhatsappBusy(false);
        return;
      }
      const result = await shareCotizacionLink(id!, {
        fileName: cotizacionFileName(header),
        pdf: blob,
      });
      shareUrl = result.shareUrl;
    } catch (err) {
      toast.error('No se pudo generar el link al PDF', {
        description:
          err instanceof Error
            ? err.message
            : 'Revisá tu conexión y volvé a intentar.',
      });
      setWhatsappBusy(false);
      return;
    }

    const summaryLines = [
      `*${title} N° ${nro}*`,
      '',
      `Cliente: ${cliente}`,
      `Fecha: ${fecha}`,
      '',
      ...itemLines,
      '',
      `Subtotal: ${formatMoney(totals.subtotalDescontado, moneda)}`,
      `IVA: ${formatMoney(ivaTotal, moneda)}`,
      `*Total: ${formatMoney(totals.total, moneda)}*`,
      '',
      shareUrl,
    ];
    const text = summaryLines.join('\n');

    const base = phone ? `https://wa.me/${phone}` : 'https://wa.me/';
    const target = `${base}?text=${encodeURIComponent(text)}`;
    // Open in a new tab and never touch the current one. Don't try to
    // detect popup blocking via the return value — `window.open` with
    // `noopener` returns null even on success (MDN), so the previous
    // `if (!opened) location.href = ...` fallback fired on every call
    // and dragged the current tab along.
    window.open(target, '_blank', 'noopener,noreferrer');
    setWhatsappBusy(false);
  };

  const aceptarMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('No id');

      // Live stock revalidation — PowerApps re-queries 18.Inventario at
      // click time and blocks acceptance if any line is short.
      //
      // Pool: Disponible + Por Ingresar (calcStockCotizable). Strict
      // Disponible-only used to lock the whole pipeline whenever the
      // admin in charge of moving Por Ingresar → Disponible was slow,
      // even though the units physically existed and were already
      // paid for. The downstream debit logic (the post-acceptance
      // status transitions) already drains Disponible first and falls
      // back to Por Ingresar, so accepting against the wider pool is
      // safe — the bucketing settles itself when the row advances.
      // Lines are fetched here if the background query hasn't
      // resolved yet so the user never has to wait for them on the
      // page itself.
      const [freshInv, freshLines] = await Promise.all([
        inventarioClient.listItems({
          filter: "fields/Status_I eq 'Activo'",
          top: 5000,
        }),
        linesQ.data ? Promise.resolve(linesQ.data) : listDetallesByOperacion(id),
      ]);
      const freshByArticulo = new Map<string, InventarioItem>();
      for (const inv of freshInv) {
        const aid = inv.fields.IDArticulo_I;
        if (aid) freshByArticulo.set(aid, inv);
      }
      const found: StockShortage[] = [];
      for (const d of freshLines) {
        const aid = d.fields.IDArticulo_DO;
        const cantidad = parseMoney(d.fields.Cantidad_RO) ?? 0;
        const disponible = calcStockCotizable(aid ? freshByArticulo.get(aid) : null);
        if (aid && cantidad > disponible) {
          found.push({
            articulo: d.fields.Articulo_DO?.trim() || '—',
            requerido: cantidad,
            disponible,
          });
        }
      }
      if (found.length > 0) throw new StockShortageError(found);

      return resumenOperacionesClient.updateItem(id, {
        Status_RO: 'Aceptado',
        Aceptado_RO: 'SI',
      });
    },
    onSuccess: () => {
      setConfirmAceptarOpen(false);
      qc.invalidateQueries({ queryKey: ['detalle-venta'] });
      qc.invalidateQueries({ queryKey: ['ventas'] });
      // Pass `justAccepted` so the Pagos screen knows we just flipped
      // the venta to Aceptado and can guard against the user leaving
      // without saving medios — if they do, we revert the status.
      navigate(`/ventas/${id}/pagos`, { state: { justAccepted: true } });
    },
    onError: (err) => {
      setConfirmAceptarOpen(false);
      if (err instanceof StockShortageError) {
        setShortages(err.shortages);
        return;
      }
      // eslint-disable-next-line no-console
      console.error('[ventas] aceptar failed', err);
      setErrorMsg('No se pudo aceptar la cotización. Intentá nuevamente en unos minutos.');
    },
  });

  const anularMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('No id');
      return anularVenta(id);
    },
    onSuccess: (r) => {
      setConfirmAnularOpen(false);
      // Internal alert email is fire-and-forget — the venta is already
      // anulada, so we never block on it. A failed alert just surfaces
      // a soft warning toast for traceability and moves on.
      if (r.email && !r.email.sent) {
        toast.warning('Cotización anulada', {
          description: 'No se pudo enviar el aviso interno. Avisá a Sumar manualmente si hace falta.',
        });
      }
      qc.invalidateQueries({ queryKey: ['detalle-venta'] });
      qc.invalidateQueries({ queryKey: ['ventas'] });
    },
    onError: (err) => {
      setConfirmAnularOpen(false);
      // eslint-disable-next-line no-console
      console.error('[ventas] anular failed', err);
      setErrorMsg('No se pudo anular la cotización. Intentá nuevamente en unos minutos.');
    },
  });

  // --- early states ---
  // Only the header is blocking. Lines / autorizaciones / contacto load in
  // the background: the hub renders immediately and their data trickles in
  // without stalling the page.
  if (headerQ.isPending) {
    return (
      <div className="flex h-full items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
      </div>
    );
  }
  if (headerQ.isError || !header) {
    return (
      <div className="space-y-4">
        <Button variant="outline" onClick={() => navigate('/ventas')} className="gap-2">
          <ArrowLeft className="h-4 w-4" aria-hidden /> Volver
        </Button>
        <div className="rounded-2xl border border-dashed bg-card/40 p-10 text-center">
          <p className="text-base font-medium text-foreground">Operación no encontrada</p>
          <p className="mt-1 text-sm text-muted-foreground">
            La cotización #{id} no existe o no tenés permisos para verla.
          </p>
        </div>
      </div>
    );
  }


  const processing = aceptarMutation.isPending || anularMutation.isPending;
  const cotizacionPill = cotizacionPillFor(status, aceptado, anulado);
  // Null (not '—') so `InfoField` can distinguish loading from truly-empty
  // and render a skeleton while `contactoQ` is still in flight.
  const contactoName =
    contactoQ.data?.fields.Receptor_C?.trim() ||
    contactoQ.data?.fields.Referido_C?.trim() ||
    null;
  const contactoMail = header.fields.Correo_RO ?? contactoQ.data?.fields.Mail_C ?? null;
  const contactoCuit = contactoQ.data?.fields.CUIT_C ?? null;
  const contactoTel = contactoQ.data?.fields.Celular_C ?? null;

  return (
    <div className="flex h-full flex-col gap-4">
      {/* TOP TOOLBAR */}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          size="icon"
          className="h-10 w-10 shrink-0 rounded-full bg-foreground text-background hover:bg-foreground/90"
          onClick={() => navigate('/ventas')}
          aria-label="Volver a Ventas"
          title="Volver a Ventas"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          Ventas{' '}
          <span className="ml-2 text-sm font-normal text-muted-foreground">
            Fecha: {header.fields.Fecha_RO || '—'}
          </span>
        </h1>
        <EcheqSaldoTag saldo={saldoEcheq} />
      </div>

      {/* CONTACT INFO STRIP */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-2xl border bg-card p-4 md:grid-cols-5">
        <InfoField label="Nombre" value={header.fields.Cliente_RO} />
        <InfoField
          label="Mail"
          value={contactoMail}
          loading={contactoQ.isPending && !header.fields.Correo_RO}
        />
        <InfoField label="CUIT" value={contactoCuit} loading={contactoQ.isPending} />
        <InfoField label="Teléfono" value={contactoTel} loading={contactoQ.isPending} />
        <InfoField label="Contacto" value={contactoName} loading={contactoQ.isPending} />
      </div>

      {/* NOVA FLOW BANNER — server flips f_nova=true when MX + MXN +
          total > param('Limite Nova'). The orchestration only runs
          for Nova-routed medios (Nova Banorte / Nova Mercado Pago /
          Herconstru); cash, Italmexy's own bank (BBVA Italmexy), and
          other direct channels bypass Nova even on f_nova ventas. */}
      {header.fields.FNova_RO === 'SI' && (
        <div className="flex items-start gap-3 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
          <Landmark className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <div>
            <p className="font-medium">Flujo Nova activo</p>
            <p className="mt-0.5 text-xs text-indigo-900/80">
              Esta venta supera el límite Nova. Los pagos cargados con un
              medio Nova (Nova Banorte, Nova Mercado Pago, Herconstru) se
              procesarán a través de Nova como intermediario y generarán
              automáticamente la factura de costo contable y los anticipos
              de Cuentas por Cobrar. Pagos en efectivo, BBVA Italmexy u
              otros medios directos se cobran normalmente.
            </p>
          </div>
        </div>
      )}

      {/* PENDING APPROVAL ALERT */}
      {isPendingApproval && !aceptado && !anulado && (
        <div className="flex items-start gap-3 rounded-xl border border-fuchsia-200 bg-fuchsia-50 px-4 py-3 text-sm text-fuchsia-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <div>
            <p className="font-medium">
              La cotización está pendiente de aprobación.
            </p>
            <p className="mt-0.5 text-xs text-fuchsia-900/80">
              No es posible aceptarla, descargar el PDF, ni compartirla por
              email o WhatsApp hasta que sea revisada por un autorizador.
            </p>
          </div>
        </div>
      )}

      {/* STAGE HUB */}
      <div className="flex flex-col gap-3">
        {/* Cotización — always active */}
        <StageCard icon={ClipboardList} title="Cotización">
          {/* Accept / Anular are only meaningful before the cotización has
              been accepted or anulada. Hiding them (rather than disabling)
              keeps the row compact once the decision is made. */}
          {!aceptado && !anulado && canAct && (
            <>
              <IconAction
                onClick={() => setConfirmAceptarOpen(true)}
                disabled={!canAccept}
                tone="green"
                title={
                  hasPending
                    ? 'Hay aprobaciones pendientes'
                    : isAdministracion
                      ? 'El perfil Administración no puede aceptar'
                      : 'Aceptar cotización'
                }
                ariaLabel="Aceptar cotización"
              >
                <Check className="h-4 w-4" aria-hidden />
              </IconAction>
              <IconAction
                onClick={() => setConfirmAnularOpen(true)}
                disabled={!canAnular}
                tone="red"
                title={
                  isAdministracion
                    ? 'El perfil Administración no puede anular'
                    : 'Anular cotización'
                }
                ariaLabel="Anular cotización"
              >
                <X className="h-4 w-4" aria-hidden />
              </IconAction>
            </>
          )}
          <IconAction
            onClick={() => setDetalleOpen(true)}
            tone="neutral"
            title="Ver detalle de la cotización"
            ariaLabel="Ver detalle"
          >
            <Eye className="h-4 w-4" aria-hidden />
          </IconAction>
          {canAct && (
            <IconAction
              onClick={() => setEditarOpen(true)}
              disabled={!canEdit}
              tone="neutral"
              title={
                !isEditableStatus && !anulado
                  ? 'La cotización ya pasó a despacho y no puede editarse'
                  : anulado
                    ? 'La cotización está anulada'
                    : isAdministracion
                      ? 'El perfil Administración no puede editar'
                      : 'Editar cotización'
              }
              ariaLabel="Editar cotización"
            >
              <Pencil className="h-4 w-4" aria-hidden />
            </IconAction>
          )}
          {/* Email + WhatsApp SEND the cotización externally → gated on canAct
              so marketing can share its OWN ventas but not others'. Download
              (below) stays available — it's a passive export of viewable data. */}
          {canAct && (
            <>
              <IconAction
                onClick={() => setEnviarEmailOpen(true)}
                disabled={!header || linesQ.isPending || isPendingApproval}
                tone="neutral"
                title={
                  isPendingApproval
                    ? 'No se puede enviar — hay aprobaciones pendientes'
                    : !contactoMail
                      ? 'Enviar por email (sin correo cargado, podés ingresarlo en el diálogo)'
                      : `Enviar por email a ${contactoMail}`
                }
                ariaLabel="Enviar por email"
              >
                <Mail className="h-4 w-4" aria-hidden />
              </IconAction>
              <IconAction
                onClick={handleShareWhatsApp}
                disabled={whatsappBusy || !header || linesQ.isPending || isPendingApproval}
                tone="neutral"
                title={
                  isPendingApproval
                    ? 'No se puede compartir — hay aprobaciones pendientes'
                    : whatsappBusy
                      ? 'Generando link al PDF…'
                      : !contactoQ.data?.fields.Celular_C
                        ? 'Enviar por WhatsApp (sin celular cargado, abre WhatsApp Web sin destinatario)'
                        : 'Abrir WhatsApp Web con el detalle de la cotización'
                }
                ariaLabel="Enviar por WhatsApp"
              >
                {whatsappBusy ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <MessageCircle className="h-4 w-4" aria-hidden />
                )}
              </IconAction>
            </>
          )}
          <IconAction
            onClick={handleDownloadPDF}
            disabled={downloadBusy || !header || linesQ.isPending || isPendingApproval}
            tone="neutral"
            title={
              isPendingApproval
                ? 'No se puede descargar — hay aprobaciones pendientes'
                : downloadBusy
                  ? 'Generando PDF…'
                  : 'Descargar PDF de la cotización'
            }
            ariaLabel="Descargar PDF"
          >
            {downloadBusy ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Download className="h-4 w-4" aria-hidden />
            )}
          </IconAction>
          <span
            className={cn(
              'ml-1 inline-flex h-10 items-center gap-1.5 rounded-lg px-3 text-xs font-semibold',
              cotizacionPill.cls,
            )}
          >
            {cotizacionPill.label}
            {cotizacionPill.showCheck && <Check className="h-3.5 w-3.5" aria-hidden />}
          </span>
        </StageCard>

        {/* Pagos */}
        <StageCard
          icon={Wallet}
          title="Pagos"
          disabled={!stages.pagos.enabled}
          disabledHint={stages.pagos.hint}
        >
          {pagoCargado ? (
            <>
              <IconAction
                onClick={() => navigate(`/ventas/${id}/pagos`)}
                tone="neutral"
                title="Ver pagos cargados"
                ariaLabel="Ver pagos"
              >
                <Eye className="h-4 w-4" aria-hidden />
              </IconAction>
              <span className="ml-1 inline-flex h-10 items-center gap-1.5 rounded-lg bg-emerald-100 px-3 text-xs font-semibold text-emerald-700">
                Condiciones de Facturación y Pagos Cargada
                <Check className="h-3.5 w-3.5" aria-hidden />
              </span>
            </>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/ventas/${id}/pagos`)}
              disabled={!stages.pagos.enabled}
            >
              Cargar de Condiciones de Facturación y Pagos
            </Button>
          )}
        </StageCard>

        {/* Facturación */}
        <StageCard
          icon={FileText}
          title="Facturación"
          disabled={!stages.factura.enabled}
          disabledHint={stages.factura.hint}
        >
          <Button
            type="button"
            variant="outline"
            onClick={() => setVerFacturasOpen(true)}
            disabled={!stages.factura.enabled}
            className="gap-2"
          >
            <Eye className="h-4 w-4" aria-hidden /> Ver Facturas
          </Button>
        </StageCard>

        {/* Despacho */}
        <StageCard
          icon={Box}
          title="Despacho"
          disabled={!stages.despacho.enabled}
          disabledHint={stages.despacho.hint}
        >
          <Button
            type="button"
            variant="outline"
            onClick={() => setDespachoArticulosOpen(true)}
            disabled={!stages.despacho.enabled}
            className="gap-2"
          >
            <Eye className="h-4 w-4" aria-hidden /> Ver Artículos
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setDespachoDetallesOpen(true)}
            disabled={!stages.despacho.enabled}
            className="gap-2"
          >
            <Eye className="h-4 w-4" aria-hidden /> Ver Detalles del Despacho
          </Button>
          {canCambiarHorno && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setCambiarHornoOpen(true)}
              className="gap-2"
              title="Pedir el cambio de un horno (requiere aprobación)"
            >
              <Repeat className="h-4 w-4" aria-hidden /> Cambiar horno
            </Button>
          )}
        </StageCard>

        {/* Puesta en marcha */}
        <StageCard
          icon={CheckCheck}
          title="Puesta en marcha"
          disabled={!stages.puesta.enabled}
          disabledHint={stages.puesta.hint}
        >
          <Button
            type="button"
            variant="outline"
            onClick={() => setPuestaMarchaOpen(true)}
            disabled={!stages.puesta.enabled}
            className="gap-2"
          >
            <Eye className="h-4 w-4" aria-hidden /> Ver Puesta en marcha
          </Button>
        </StageCard>
      </div>

      <DetalleLineasDialog
        open={detalleOpen}
        onClose={() => setDetalleOpen(false)}
        lines={lineRows}
        invoice={invoice}
        moneda={moneda}
        title={`Cotización Nº ${header.fields.NrCotizacion_RO || header.id}`}
        loading={linesQ.isPending}
      />
      <ConfirmAceptarDialog
        open={confirmAceptarOpen && !processing}
        cliente={header.fields.Cliente_RO}
        total={invoice.total > 0 ? invoice.total : header.fields.TotalCotizacion_RO ?? 0}
        moneda={moneda}
        onCancel={() => setConfirmAceptarOpen(false)}
        onConfirm={() => aceptarMutation.mutate()}
      />
      <ConfirmAnularDialog
        open={confirmAnularOpen && !processing}
        nrCotizacion={header.fields.NrCotizacion_RO || header.id}
        pendingCount={pendingCount}
        onCancel={() => setConfirmAnularOpen(false)}
        onConfirm={() => anularMutation.mutate()}
      />
      <SinStockDialog
        open={shortages !== null}
        shortages={shortages ?? []}
        onClose={() => setShortages(null)}
      />
      <ErrorDialog
        open={errorMsg !== null}
        message={errorMsg ?? ''}
        onClose={() => setErrorMsg(null)}
      />
      {id && (
        <EditarCotizacionDialog
          open={editarOpen}
          onClose={() => setEditarOpen(false)}
          ventaId={id}
          moneda={moneda}
          vendedorApp={profile?.usuario_app ?? ''}
          initialLines={editorSeeds}
          campaignByArticulo={campaignByArticulo}
          porcentajeFactura={parseMoney(header.fields.PorcentajeFactura_RO) || 100}
          recargoMpPct={pctEfectivoRecargoMp(
            parseMoney(header.fields.TotalFactura_RO) ?? 0,
            parseMoney(header.fields.RecargoMpMonto_RO) ?? 0,
          )}
          clienteExterior={esClienteExterior(contactoQ.data?.fields.CP_C)}
        />
      )}
      {id && verFacturasOpen && (
        <VerFacturasDialog
          ventaId={id}
          moneda={moneda}
          ventaTotal={
            (Number(header?.fields.TotalCotizacion_RO) || 0) +
            (parseMoney(header?.fields.RecargoMpMonto_RO) ?? 0)
          }
          onClose={() => setVerFacturasOpen(false)}
        />
      )}
      <EmailConfirmDialog
        open={enviarEmailOpen}
        title="Enviar cotización por correo"
        description={
          (contactoQ.data?.fields.NombreApellido_C?.trim() || header.fields.Cliente_RO?.trim())
            ? `El correo se enviará a ${contactoQ.data?.fields.NombreApellido_C?.trim() || header.fields.Cliente_RO?.trim()} con la cotización adjunta en PDF.`
            : 'El correo incluirá la cotización adjunta en PDF.'
        }
        defaultEmail={contactoMail ?? ''}
        onCancel={() => setEnviarEmailOpen(false)}
        onSubmit={handleEnviarEmail}
      />
      <DespachoDetallesDialog
        open={despachoDetallesOpen}
        onClose={() => setDespachoDetallesOpen(false)}
        recibe={header.fields.RecibeDespacho_RO}
        direccion={header.fields.DireccionCompletaDespacho_RO}
        observaciones={header.fields.ObsDespacho_RO}
        fotoIdsJson={header.fields.DespachoFotoIds_RO}
        fotoTiposJson={header.fields.DespachoFotoTipos_RO}
      />
      {id && (
        <DespachoArticulosDialog
          open={despachoArticulosOpen}
          onClose={() => setDespachoArticulosOpen(false)}
          ventaId={id}
        />
      )}
      {id && cambiarHornoOpen && (
        <CambiarHornoDialog
          ventaId={id}
          moneda={monedaCambiarHorno}
          porcentajeFactura={porcentajeFacturaVenta}
          detalles={cambiarHornoDetallesQ.data ?? []}
          clienteExterior={esClienteExterior(contactoQ.data?.fields.CP_C)}
          onClose={() => setCambiarHornoOpen(false)}
        />
      )}
      {id && (
        <PuestaMarchaDetallesDialog
          open={puestaMarchaOpen}
          onClose={() => setPuestaMarchaOpen(false)}
          ventaId={id}
          instalacionId={header.fields.VideoInstalacionDriveItemID_RO}
          llamaId={header.fields.VideoLlamaDriveItemID_RO}
          reguladorId={header.fields.VideoReguladorDriveItemID_RO}
          multimediaId={header.fields.VideoMultimediaDriveItemID_RO}
        />
      )}
      {aceptarMutation.isPending && <ProcesandoOverlay message="Aceptando cotización…" />}
      {anularMutation.isPending && <ProcesandoOverlay message="Anulando cotización…" />}
    </div>
  );
}

// --------------------------------------------------------------------------
// Stage-gating logic
// --------------------------------------------------------------------------

type StageState = { enabled: boolean; hint?: string };
type StageStates = {
  cotizacion: { canAccept: boolean; canAnular: boolean };
  pagos: StageState;
  factura: StageState;
  despacho: StageState;
  puesta: StageState;
};

const PAGOS_STATUSES = new Set([
  'Aceptado',
  'Pendiente de Pago',
  'Confirmacion de Pago',
  'Pago Parcial',
  'Pago Realizado',
  'Pendiente Despacho',
  'Despachado',
  'Pendiente Puesta Marcha',
  'Cerrado',
]);
const FACTURA_STATUSES = new Set([
  'Pago Realizado',
  'Pendiente Despacho',
  'Despachado',
  'Pendiente Puesta Marcha',
  'Cerrado',
]);
const DESPACHO_STATUSES = new Set([
  'Pendiente Despacho',
  'Despachado',
  'Pendiente Puesta Marcha',
  'Cerrado',
]);
const PUESTA_STATUSES = new Set([
  'Pendiente Puesta Marcha',
  'Despachado',
  'Cerrado',
]);

/**
 * Map a server-side email send error into a Spanish operator-facing
 * message. We get raw Graph strings back via `result.error` when the
 * server can't reach the FROM mailbox; the raw English doesn't help
 * the operator know what action to take. Add cases here as new Graph
 * errors surface in the wild.
 */
function friendlyEmailSendError(error: string | undefined): string {
  if (!error) return 'Error desconocido';
  // Graph's `MailboxNotEnabledForRESTAPI` / equivalent rejection — fires
  // when the FROM mailbox is unlicensed, soft-deleted, or hosted on a
  // non-Exchange-Online server. Surfaces in prod whenever the
  // `MAIL_FROM` Vercel env var (or its hardcoded default) points at a
  // mailbox that's no longer sendable.
  if (/mailbox is either inactive|soft-deleted|on-premise/i.test(error)) {
    return 'La cuenta de correo está inactiva o deshabilitada. Contactá al administrador.';
  }
  return error;
}

function deriveStageStates(args: {
  status: string | null;
  aceptado: boolean;
  pagoCargado: boolean;
  anulado: boolean;
  /** La venta anulada todavía tiene facturas emitidas ante ARCA. */
  tieneFacturasVivas?: boolean;
}): StageStates {
  const { status, aceptado, pagoCargado, anulado, tieneFacturasVivas } = args;

  if (anulado) {
    const hint = 'La cotización fue anulada.';
    return {
      cotizacion: { canAccept: false, canAnular: false },
      pagos: { enabled: false, hint },
      // Excepción: si quedaron facturas emitidas, Facturación sigue accesible
      // (sólo lectura + Generar NC). Es el único camino para darlas de baja.
      factura: tieneFacturasVivas
        ? {
            enabled: true,
            hint: undefined,
          }
        : { enabled: false, hint },
      despacho: { enabled: false, hint },
      puesta: { enabled: false, hint },
    };
  }

  const canAccept = !aceptado && status !== 'Pendiente Aprobacion';
  const canAnular = !aceptado;

  const pagosEnabled = aceptado || (status != null && PAGOS_STATUSES.has(status));
  const facturaEnabled =
    (status != null && FACTURA_STATUSES.has(status)) || (aceptado && pagoCargado);
  const despachoEnabled = status != null && DESPACHO_STATUSES.has(status);
  const puestaEnabled = status != null && PUESTA_STATUSES.has(status);

  const pendingHint = 'Esta sección se habilita una vez avanzada la cotización.';
  return {
    cotizacion: { canAccept, canAnular },
    pagos: { enabled: pagosEnabled, hint: pagosEnabled ? undefined : pendingHint },
    factura: { enabled: facturaEnabled, hint: facturaEnabled ? undefined : pendingHint },
    despacho: { enabled: despachoEnabled, hint: despachoEnabled ? undefined : pendingHint },
    puesta: { enabled: puestaEnabled, hint: puestaEnabled ? undefined : pendingHint },
  };
}

function cotizacionPillFor(
  status: string | null,
  aceptado: boolean,
  anulado: boolean,
): { label: string; cls: string; showCheck: boolean } {
  if (anulado) {
    return {
      label: 'Cotización Anulada',
      cls: 'bg-rose-100 text-rose-700',
      showCheck: false,
    };
  }
  if (status === 'Pendiente Aprobacion') {
    return {
      label: 'Aprobación Requerida',
      cls: 'bg-fuchsia-100 text-fuchsia-700',
      showCheck: false,
    };
  }
  if (aceptado || (status && status !== 'Cotizado')) {
    return {
      label: 'Cotización Aceptada',
      cls: 'bg-emerald-100 text-emerald-700',
      showCheck: true,
    };
  }
  return {
    label: 'Cotización Realizada',
    cls: 'bg-emerald-100 text-emerald-700',
    showCheck: true,
  };
}

// --------------------------------------------------------------------------
// Small presentational helpers
// --------------------------------------------------------------------------

class StockShortageError extends Error {
  constructor(public readonly shortages: StockShortage[]) {
    super('stock_shortage');
    this.name = 'StockShortageError';
  }
}


type IconTone = 'green' | 'red' | 'neutral';

const TONE_CLASSES: Record<IconTone, string> = {
  green:
    'border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:border-border disabled:bg-muted disabled:text-muted-foreground',
  red:
    'border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100 disabled:border-border disabled:bg-muted disabled:text-muted-foreground',
  neutral:
    'border-border bg-card text-foreground hover:bg-muted disabled:bg-muted disabled:text-muted-foreground',
};

function IconAction({
  onClick,
  disabled = false,
  tone = 'neutral',
  title,
  ariaLabel,
  children,
}: {
  onClick?: () => void;
  disabled?: boolean;
  tone?: IconTone;
  title?: string;
  ariaLabel: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel}
      className={cn(
        'inline-flex h-10 w-10 items-center justify-center rounded-lg border transition-colors disabled:cursor-not-allowed disabled:opacity-60',
        TONE_CLASSES[tone],
      )}
    >
      {children}
    </button>
  );
}

function toLineRow(
  d: DetalleOperacionesItem,
  campaigns: Map<string, number>,
  pendingByDetalle: Set<string>,
): DetalleLineaRow {
  const f = d.fields;
  const monto = parseMoney(f.Monto_RO) ?? 0;
  const cantidad = parseMoney(f.Cantidad_RO) ?? 0;
  const descuento = parseMoney(f.Descuento_RO) ?? 0;
  const iva: IvaRate = parseIvaRateString(f.IVA_RO);
  const esRegalo = f.EsRegalo_DO === 'SI';
  const articuloId = f.IDArticulo_DO ?? '';
  const campaignPct = campaigns.get(articuloId) ?? null;
  return {
    detalleId: d.id,
    articulo: f.Articulo_DO?.trim() || '—',
    monto,
    cantidad,
    descuento,
    iva,
    esRegalo,
    campaignPct,
    needsAuthorization: pendingByDetalle.has(d.id),
    totals: calcLineTotals({ monto, cantidad, descuento, iva, esRegalo }),
    llevaGarantia: f.LlevaGarantia_DO === 'SI',
    garantiaMeses: mesesGarantiaDe(f.GarantiaMeses_DO),
    garantiaVence: f.GarantiaVence_DO ?? null,
    fechaEstimadaIngreso: f.FechaEstimadaIngreso_DO ?? null,
  };
}

/**
 * Fetch an articulo image as a base64 data URL — jsPDF's `addImage`
 * needs a synchronous data URL, not a blob URL, so we read the blob
 * through FileReader. Returns `null` on 404 (no image), HTTP error,
 * or read failure. Surfaced errors are intentionally swallowed: a
 * missing image should never block PDF generation.
 */
async function fetchArticuloImageAsDataUrl(
  articuloId: string,
): Promise<string | null> {
  try {
    const res = await fetch(articuloImageUrl(articuloId), {
      headers: authHeaders(),
    });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// --------------------------------------------------------------------------
// Confirmation dialogs
// --------------------------------------------------------------------------

function ConfirmAceptarDialog({
  open,
  cliente,
  total,
  moneda,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  cliente: string | null | undefined;
  total: number;
  moneda: Moneda;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Aceptar cotización</DialogTitle>
          <DialogDescription>
            Vas a aceptar la cotización. Se validará el stock disponible y, si
            todo está en orden, pasará al circuito de pagos.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Cliente</dt>
            <dd className="font-medium text-foreground">{cliente?.trim() || '—'}</dd>
            <dt className="text-muted-foreground">Total</dt>
            <dd className="font-semibold text-foreground">
              <MoneyCell amount={total} moneda={moneda} emphasise />
            </dd>
          </dl>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            className="gap-2 bg-foreground text-background hover:bg-foreground/90"
          >
            <Check className="h-4 w-4" aria-hidden /> Aceptar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmAnularDialog({
  open,
  nrCotizacion,
  pendingCount,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  nrCotizacion: string;
  pendingCount: number;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-600">
              <Ban className="h-5 w-5" aria-hidden />
            </span>
            <div>
              <DialogTitle>Anular cotización</DialogTitle>
              <DialogDescription>
                Vas a anular la cotización Nº{' '}
                <span className="font-semibold text-foreground">{nrCotizacion}</span>.
                {pendingCount > 0
                  ? ` También se anularán ${pendingCount} solicitud${
                      pendingCount > 1 ? 'es' : ''
                    } de aprobación pendiente${pendingCount > 1 ? 's' : ''}.`
                  : ' Esta acción no se puede deshacer desde acá.'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            Volver
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            className="gap-2 bg-rose-600 text-white hover:bg-rose-700"
          >
            <Ban className="h-4 w-4" aria-hidden /> Anular
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
